/**
 * Axios request and response interceptors
 * Handles authentication tokens and error responses
 */

import { AxiosError, AxiosRequestConfig } from 'axios';

import { emitApiError, endRequest, startRequest } from '../services/apiProgress';

import apiClient from './client';

type RetryableRequest = AxiosRequestConfig & { _retry?: boolean };

// Access token lives in memory only — never written to localStorage (XSS protection)
let memoryAccessToken: string | null = null;

export function setAccessToken(token: string): void {
  memoryAccessToken = token;
}

export function clearAccessToken(): void {
  memoryAccessToken = null;
}

// Single shared promise so concurrent 401s only trigger one refresh attempt
let refreshPromise: Promise<string> | null = null;

// Request interceptor — add auth token + increment active-request counter
apiClient.interceptors.request.use(
  (config) => {
    if (memoryAccessToken) {
      config.headers.Authorization = `Bearer ${memoryAccessToken}`;
    }
    startRequest();
    return config;
  },
  (error) => {
    endRequest();
    return Promise.reject(error);
  }
);

// Response interceptor — decrement counter + handle 401 with token refresh
apiClient.interceptors.response.use(
  (response) => {
    endRequest();
    return response;
  },
  async (error: AxiosError) => {
    endRequest();
    const originalRequest = error.config as RetryableRequest | undefined;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    // Auth endpoints (login, register, refresh, etc.) must not trigger token refresh.
    // If refresh-token fails, clear the access token and let the caller handle it.
    // AuthContext.checkAuth() already gracefully handles a failed refresh by setting
    // user=null; ProtectedRoute then redirects protected pages to login as needed.
    if (originalRequest.url?.includes('/auth/')) {
      if (originalRequest.url?.includes('/auth/refresh-token')) {
        clearAccessToken();
      }
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized — refresh once using the httpOnly cookie (sent automatically),
    // queue all concurrent 401s behind the same promise so only one refresh fires
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        if (!refreshPromise) {
          // No body needed — the httpOnly cookie is sent automatically (withCredentials: true)
          refreshPromise = apiClient
            .post('/auth/refresh-token')
            .then((res) => {
              const token: string = res.data.data.accessToken;
              setAccessToken(token);
              return token;
            })
            .finally(() => {
              refreshPromise = null;
            });
        }

        const newToken = await refreshPromise;
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${newToken}`,
        };
        return apiClient(originalRequest);
      } catch (refreshError) {
        refreshPromise = null;
        clearAccessToken();
        if (!window.location.pathname.startsWith('/auth/')) {
          window.location.href = '/auth/login';
        }
        return Promise.reject(refreshError);
      }
    }

    // ── Global error notifications for non-auth failures ─────────────────────
    const status = error.response?.status;

    if (!error.response) {
      // Network error — server unreachable
      emitApiError({ message: 'Unable to reach the server. Check your connection.' });
    } else if (status === 429) {
      emitApiError({ status, message: 'Too many requests. Please slow down and try again.' });
    } else if (status !== undefined && status >= 500) {
      emitApiError({ status, message: 'A server error occurred. Our team has been notified.' });
    }

    return Promise.reject(error);
  }
);

export default apiClient;
